import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';
// Stable class name the TreeWidget stamps on each row's checkmark-container span
// (exported from the widget's public typings). Targeting it lets us collapse the
// widget's built-in checkmark column without depending on DOM child order.
import { CheckContainerStyleName } from '@tableau/widgets/lib/src/widgets/Tree/TreeStyles';

import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

import { ERROR_LIGHT_FALLBACK } from '../routingFilterTable/routingFilterTableStyles';

export const projectDropdownContainerClass = cssClass('project-multiselect-tree-dropdown', 'container', {
  marginTop: TabStyles.Spacing.SmallSpace,
  marginBottom: TabStyles.Spacing.LargeSpace,
});

export const projectDropdownLabelClass = cssClass('project-multiselect-tree-dropdown', 'label', {
  display: 'block',
  marginBottom: TabStyles.Spacing.SmallSpace,
});

export const projectDropdownButtonClass = cssClass('project-multiselect-tree-dropdown', 'button', {
  width: '100%',
});

export const projectTreeNodeTextStyle = cssClass('project-multiselect-tree-dropdown', 'tree-node-text', {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Error highlight on a conflicting project row: an error-light fill shaped and
// sized to match the tree's own row hover highlight, so the persistent conflict
// tint and the transient hover read as the same element — same 2px-rounded
// rectangle spanning the full row, same height.
//
// Our content lives in a slot deep inside the row (after the chevron/checkbox
// columns and inside the label's trailing padding), so it can't paint the row
// edges directly. Instead of bleeding out with measured negative margins — which
// reached the left edge but left the hover gray showing on the right, because the
// slot's right edge never coincides with the row's — we paint a `::before`
// overlay. TreeWidget rows are made `position: relative` (see projectTreeClass),
// so this absolutely-positioned pseudo anchors to the ROW, not the slot, and
// `inset: 0` fills it corner to corner regardless of window size or column
// widths — no magic numbers, fully responsive, matching how the row hover fills.
// It sits at zIndex -1 so the row's gray hover paints behind it and the checkbox,
// label, and italic "(conflict)" annotation paint in front. No border; the
// annotation carries the non-color signal (WCAG 1.4.1).
export const projectTreeNodeConflictClass = cssClass('project-multiselect-tree-dropdown', 'tree-node-conflict', {
  $nest: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundColor: applyColor(DesignTokens.color.component.background.alert.default.error.default, ERROR_LIGHT_FALLBACK),
      borderRadius: 2,
      zIndex: -1,
    },
  },
});

// Italic error-colored "(conflict)" text appended to a conflicting project's
// label, matching ProjectsInfoPopover's conflict annotation. Provides a
// non-color signal for the conflict (the tinted row alone would fail WCAG 1.4.1).
export const projectTreeNodeConflictAnnotationClass = cssClass('project-multiselect-tree-dropdown', 'tree-node-conflict-annotation', {
  flexShrink: 0,
  // Sits on the conflict row's alert fill (see projectTreeNodeConflictClass), so
  // use the paired content-on-alert token — dark red on the light fill, light
  // pink on the dark-theme maroon — rather than a fixed red that fades on dark.
  color: applyColor(DesignTokens.color.component.content.alert.default.error.default, '#ba0517'),
  fontStyle: 'italic',
});

export const projectTreeNodeFolderIconClass = cssClass('project-multiselect-tree-dropdown', 'tree-node-folder-icon', {
  display: 'none',
  flexShrink: 0,
});

export const projectTreeNodeMoreButtonClass = cssClass('project-multiselect-tree-dropdown', 'tree-node-more-button', {
  marginLeft: 'auto',
  flexShrink: 0,
  background: 'transparent',
  border: 0,
  padding: TabStyles.Spacing.SmallSpace,
  cursor: 'pointer',
  display: 'none',
  alignItems: 'center',
});

export const projectTreeNodeStyle = cssClass('project-multiselect-tree-dropdown', 'tree-node', {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: TabStyles.Spacing.SmallSpace,
  $nest: {
    [`&:hover .${projectTreeNodeFolderIconClass}`]: {
      display: 'flex',
    },
    [`&:hover .${projectTreeNodeMoreButtonClass}`]: {
      display: 'flex',
    },
  },
});

export const projectPickerSelectAllRowClass = cssClass('project-multiselect-tree-dropdown', 'select-all-row', {
  display: 'flex',
  gap: TabStyles.Spacing.MediumSpace,
  paddingTop: TabStyles.Spacing.SmallSpace,
  paddingLeft: TabStyles.Spacing.DefaultSpace,
  paddingBottom: TabStyles.Spacing.SmallSpace,
});

export const projectPickerHeaderClass = cssClass('project-multiselect-tree-dropdown', 'picker-header', {
  paddingTop: TabStyles.Spacing.DefaultSpace,
  paddingLeft: TabStyles.Spacing.DefaultSpace,
  paddingRight: TabStyles.Spacing.DefaultSpace,
});

export const projectPickerTabsClass = cssClass('project-multiselect-tree-dropdown', 'picker-tabs', {
  display: 'flex',
  gap: TabStyles.Spacing.SmallSpace,
  marginBottom: TabStyles.Spacing.DefaultSpace,
});

export const projectTreeClass = cssClass('project-multiselect-tree-dropdown', 'tree', {
  // Trim the tree's own left gutter to hand more width to project names. The row
  // still keeps the chevron column below, so names don't run into the edge.
  paddingLeft: TabStyles.Spacing.SmallSpace,
  $nest: {
    // Anchor the conflict row's ::before overlay to the row itself. Each treeitem
    // becomes the overlay's containing block (position: relative) so `inset: 0`
    // fills the full row, and its own stacking context (z-index: 0) so the
    // overlay's `z-index: -1` layers above the row's gray hover background yet
    // below the checkbox/label — pink spans edge to edge, hover shows behind it,
    // text stays on top. Applied to every row (harmless for non-conflict rows).
    '& [role="treeitem"]': {
      position: 'relative',
      zIndex: 0,
    },
    // Reclaim the widget's built-in checkmark column. This tree is single-select
    // (selectedValue={null}) and we render our OWN CheckBoxWidget inside each
    // row's content, so the widget's leading checkmark box never shows a mark and
    // is pure dead space (~17px) before every row. Collapsing it shifts the
    // checkbox and name uniformly left across all rows — alignment preserved —
    // giving long project names more room. We target the checkmark span by the
    // widget's own exported class (CheckContainerStyleName) rather than a
    // positional `> span:first-child`, so a change to the row's child order
    // wouldn't silently collapse the wrong element, and dropping the export would
    // fail the build instead.
    [`& [role="treeitem"] .${CheckContainerStyleName}`]: {
      width: 0,
      margin: 0,
      overflow: 'hidden',
    },
  },
});
